/**
 * Content Matcher Utility
 *
 * Smart content matching system that finds the best matching content item
 * based on user input, keywords, and priority scoring.
 *
 * @module contentMatcher
 */

/**
 * Find best matching content item based on user input
 *
 * @param {string} input - User's question/query
 * @param {Array<Object>} contentLibrary - Array of content items with keywords
 * @param {Object} config - Matching configuration options
 * @param {boolean} [config.fuzzyMatch=true] - Enable fuzzy matching
 * @param {boolean} [config.caseSensitive=false] - Enable case-sensitive matching
 * @param {number} [config.priorityWeight=0.3] - Weight for priority scoring (0-1)
 * @param {number} [config.keywordWeight=0.7] - Weight for keyword matching (0-1)
 * @param {number} [config.minScore=0.3] - Minimum score threshold for match (0-1)
 * @returns {Object|null} Best matching content item or null if no match above threshold
 *
 * @example
 * const library = [
 *   { id: "nec", keywords: ["nec", "research"], priority: 9, content: "..." },
 *   { id: "fluxtrade", keywords: ["fluxtrade"], priority: 10, content: "..." }
 * ];
 *
 * const result = findBestMatch("tell me about nec research", library);
 * // Returns: { id: "nec", keywords: [...], priority: 9, content: "..." }
 */
const configuredAliasPattern =
  /^[\p{L}\p{N}][\p{L}\p{N}\p{M}]*(?:['’][\p{L}\p{N}][\p{L}\p{N}\p{M}]*)*(?:(?:\p{Zs}+|\p{Dash_Punctuation})[\p{L}\p{N}][\p{L}\p{N}\p{M}]*(?:['’][\p{L}\p{N}][\p{L}\p{N}\p{M}]*)*)*$/u;

function normalizeKeyword(keyword, caseSensitive) {
  if (typeof keyword !== 'string') return null;

  const canonicalKeyword = keyword.normalize('NFC');
  const normalizedKeyword = caseSensitive
    ? canonicalKeyword
    : canonicalKeyword.toLowerCase();
  if (!configuredAliasPattern.test(normalizedKeyword)) {
    return null;
  }

  const keywordParts = normalizedKeyword
    .split(/(?:\p{Zs}+|\p{Dash_Punctuation})/u)

  return keywordParts
    .map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('[\\s\\p{Dash_Punctuation}]+');
}

function matchesKeyword(input, normalizedKeyword) {
  if (!normalizedKeyword) return false;

  return new RegExp(
    `(?<![\\p{L}\\p{N}\\p{M}_])${normalizedKeyword}(?![\\p{L}\\p{N}\\p{M}_])`,
    'u'
  ).test(input);
}

function scoreItem(item, input, config) {
  if (!item || typeof item !== 'object' || !Array.isArray(item.keywords)) {
    return { item, score: 0, matchedKeywords: [] };
  }

  const validKeywords = [...new Set(
    item.keywords
      .map(keyword => normalizeKeyword(keyword, config.caseSensitive))
      .filter(Boolean)
  )];
  const matchedKeywords = validKeywords.filter(keyword => {
    return matchesKeyword(input, keyword);
  });
  const contextKeywords = Array.isArray(item.contextKeywords)
    ? [...new Set(
      item.contextKeywords
        .map(keyword => normalizeKeyword(keyword, config.caseSensitive))
        .filter(Boolean)
    )]
    : [];
  const hasContext = matchedKeywords.length > 0 &&
    contextKeywords.some(keyword => matchesKeyword(input, keyword));
  const keywordScore = hasContext
    ? 1
    : validKeywords.length > 0
      ? matchedKeywords.length / validKeywords.length
      : 0;
  const priority = typeof item.priority === 'number' ? item.priority : 5;
  const priorityScore = Math.max(0, Math.min(10, priority)) / 10;
  const score = matchedKeywords.length === 0
    ? 0
    : (keywordScore * config.keywordWeight) +
      (priorityScore * config.priorityWeight);

  return { item, score, matchedKeywords };
}

export function findBestMatch(input, contentLibrary, config = {}) {
  // Default configuration
  const defaultConfig = {
    fuzzyMatch: true,
    caseSensitive: false,
    priorityWeight: 0.3,
    keywordWeight: 0.7,
    minScore: 0.3
  };

  // Merge with provided config
  const finalConfig = { ...defaultConfig, ...config };

  // Validate input
  if (!input || typeof input !== 'string') {
    return null;
  }

  if (!contentLibrary || !Array.isArray(contentLibrary) || contentLibrary.length === 0) {
    return null;
  }

  // Normalize input based on case sensitivity
  const canonicalInput = input.normalize('NFC');
  const normalizedInput = finalConfig.caseSensitive
    ? canonicalInput
    : canonicalInput.toLowerCase();

  // Score each content item
  const scored = contentLibrary.map(item => (
    scoreItem(item, normalizedInput, finalConfig)
  ));

  // Sort by score descending
  const sorted = scored
    .filter(result => result.matchedKeywords.length > 0)
    .sort((a, b) => b.score - a.score);

  // Get best match
  const best = sorted[0];

  // Return item if score meets threshold, otherwise null
  if (best && best.score >= finalConfig.minScore) {
    return best.item;
  }

  return null;
}

/**
 * Find all matching content items above threshold
 *
 * @param {string} input - User's question/query
 * @param {Array<Object>} contentLibrary - Array of content items
 * @param {Object} config - Matching configuration
 * @param {number} [limit=5] - Maximum number of results to return
 * @returns {Array<Object>} Array of matching items sorted by score
 */
export function findAllMatches(input, contentLibrary, config = {}, limit = 5) {
  const defaultConfig = {
    fuzzyMatch: true,
    caseSensitive: false,
    priorityWeight: 0.3,
    keywordWeight: 0.7,
    minScore: 0.3
  };

  const finalConfig = { ...defaultConfig, ...config };

  if (!input || typeof input !== 'string') {
    return [];
  }

  if (!contentLibrary || !Array.isArray(contentLibrary) || contentLibrary.length === 0) {
    return [];
  }

  const canonicalInput = input.normalize('NFC');
  const normalizedInput = finalConfig.caseSensitive
    ? canonicalInput
    : canonicalInput.toLowerCase();

  // Score and filter all items
  const matches = contentLibrary
    .map(item => scoreItem(item, normalizedInput, finalConfig))
    .filter(result => (
      result.matchedKeywords.length > 0 &&
      result.score >= finalConfig.minScore
    ))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(result => result.item);

  return matches;
}

const contentMatcher = { findBestMatch, findAllMatches };
export default contentMatcher;
